// aifab - Documents panel (Data page tab)
// Unstructured document ingestion: upload files or register a server path,
// optionally merge many documents into ONE corpus datasource (document set).
// A finished document/set produces a normal datasource (single `text` column,
// already profiled) ready for Domain Pre-training feature configs.
import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { apiFetch } from '../../hooks/apiFetch';
import { Spinner, Err } from '../common';

const ACTIVE = ['QUEUED','FETCHING','FETCHED','EXTRACTING','EXTRACTED','CHUNKING','FINALIZING'];
const DOC_BADGE = {
  UPLOADED:'b-slate', REGISTERED:'b-slate', QUEUED:'b-blue', FETCHING:'b-blue',
  FETCHED:'b-blue', EXTRACTING:'b-amber', EXTRACTED:'b-amber', CHUNKING:'b-amber',
  CHUNKED:'b-green', FINALIZING:'b-amber', READY:'b-green', FAILED:'b-red',
  OPEN:'b-slate',
};

const Badge = ({ s }) => <span className={`badge ${DOC_BADGE[s]||'b-slate'}`}>{s}</span>;

export default function DocumentsPanel({ projectId, servers, onDatasourceReady }) {
  const { data: docs, reload: reloadDocs } = useApi(`/documents?project_id=${projectId}`);
  const { data: sets, reload: reloadSets } = useApi(`/documents/sets?project_id=${projectId}`);

  const [mode,     setMode]     = useState('upload');      // upload | path
  const [merge,    setMerge]    = useState(false);
  const [setName,  setSetName]  = useState('');
  const [serverId, setServerId] = useState('');
  const [strategy, setStrategy] = useState('paragraph');
  const [sizeTok,  setSizeTok]  = useState(200);
  const [overTok,  setOverTok]  = useState(20);
  const [files,    setFiles]    = useState([]);
  const [path,     setPath]     = useState('');
  const [docName,  setDocName]  = useState('');
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState(null);
  const fileRef = useRef(null);

  // Poll while anything is in-flight
  useEffect(() => {
    const active = (docs||[]).some(d => ACTIVE.includes(d.status))
                || (sets||[]).some(s => s.status === 'FINALIZING');
    if (!active) return;
    const t = setInterval(() => { reloadDocs(); reloadSets(); }, 5000);
    return () => clearInterval(t);
  }, [docs, sets, reloadDocs, reloadSets]);

  const chunkFields = () => {
    const fd = new FormData();
    fd.append('chunk_strategy', strategy);
    if (strategy === 'fixed_window') {
      fd.append('chunk_size_tokens', sizeTok);
      fd.append('chunk_overlap_tokens', overTok);
    }
    return fd;
  };

  const submit = async () => {
    setErr(null);
    if (!serverId) { setErr('Select an execution server.'); return; }
    if (mode === 'upload' && files.length === 0) { setErr('Choose at least one file.'); return; }
    if (mode === 'path' && !path.trim()) { setErr('Enter the file path on the server.'); return; }
    if (merge && !setName.trim()) { setErr('Name the corpus set.'); return; }

    setBusy(true);
    try {
      let sid = null;
      if (merge) {
        const fd = new FormData();
        fd.append('project_id', projectId);
        fd.append('server_id', serverId);
        fd.append('name', setName.trim());
        const r = await apiFetch('/documents/sets', { method: 'POST', body: fd, isFormData: true });
        sid = r.set_id;
      }

      if (mode === 'upload') {
        const docIds = [];
        for (const f of files) {
          const fd = chunkFields();
          fd.append('project_id', projectId);
          fd.append('server_id', serverId);
          fd.append('name', f.name.replace(/\.[^.]+$/, ''));
          if (sid) fd.append('set_id', sid);
          fd.append('file', f);
          const r = await apiFetch('/documents/upload', { method: 'POST', body: fd, isFormData: true });
          docIds.push(r.document_id);
        }
        if (sid) {
          await apiFetch(`/documents/sets/${sid}/process-all`, { method: 'POST' });
        } else {
          for (const id of docIds) {
            await apiFetch(`/documents/${id}/process`, { method: 'POST' });
          }
        }
      } else {
        const fd = chunkFields();
        fd.append('project_id', projectId);
        fd.append('server_id', serverId);
        fd.append('name', docName.trim() || path.split('/').pop().replace(/\.[^.]+$/, ''));
        fd.append('remote_path', path.trim());
        if (sid) fd.append('set_id', sid);
        const r = await apiFetch('/documents/register', { method: 'POST', body: fd, isFormData: true });
        if (sid) await apiFetch(`/documents/sets/${sid}/process-all`, { method: 'POST' });
        else     await apiFetch(`/documents/${r.document_id}/process`, { method: 'POST' });
      }

      setFiles([]); setPath(''); setDocName(''); setSetName(''); setMerge(false);
      if (fileRef.current) fileRef.current.value = '';
      reloadDocs(); reloadSets();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const finalizeSet = async (sid) => {
    setErr(null);
    try { await apiFetch(`/documents/sets/${sid}/finalize`, { method: 'POST' }); reloadSets(); }
    catch (e) { setErr(e.message); }
  };

  const standalone = (docs||[]).filter(d => !d.set_id);

  const input = { width:'100%', boxSizing:'border-box', padding:'8px 10px', background:'#F7F9FC',
    border:'1px solid var(--bd2)', borderRadius:6, fontSize:13, fontFamily:'var(--f-ui)', outline:'none' };
  const lbl = { fontSize:11, fontWeight:600, color:'#64748B', marginBottom:4,
    textTransform:'uppercase', letterSpacing:'.07em' };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'380px 1fr', gap:18, alignItems:'start' }}>

      {/* ── Left: intake form ─────────────────────────────────────────────── */}
      <div style={{ background:'#FFF', border:'1px solid #E4EBF5', borderRadius:12, padding:18 }}>

        {/* Info panel */}
        <div style={{ background:'rgba(232,71,10,.05)', border:'1px solid rgba(232,71,10,.18)',
          borderRadius:8, padding:'11px 13px', marginBottom:16, fontSize:11.5, lineHeight:1.55, color:'#475569' }}>
          <div style={{ fontWeight:700, color:'#0D1B2E', marginBottom:4 }}>Using unstructured documents</div>
          Documents are converted to training data in three steps: <b>text extraction</b> →{' '}
          <b>chunking</b> → a derived <b>single-text-column datasource</b>, profiled and ready for a{' '}
          <b>Domain Pre-training</b> feature config.
          <div style={{ marginTop:6 }}>
            <b>Supported:</b> PDF (text-based), DOCX, TXT, MD, HTML.{' '}
            Scanned/image-only PDFs are not supported (no OCR).
          </div>
          <div style={{ marginTop:6 }}>
            <b>Merge into one corpus</b> combines every document's text into a single
            datasource - use it when one training run should learn from all files together.
          </div>
        </div>

        {/* Mode toggle */}
        <div style={{ display:'flex', gap:4, background:'var(--surface-alt)', borderRadius:8,
          padding:3, border:'1px solid var(--border)', marginBottom:14 }}>
          {[['upload','Upload files'],['path','Register path']].map(([m,l]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex:1, padding:'6px 0', border:'none', borderRadius:6, cursor:'pointer',
              fontSize:12, fontWeight:600, fontFamily:'inherit',
              background: mode===m ? '#E8470A' : 'transparent',
              color: mode===m ? '#fff' : 'var(--text-muted)',
            }}>{l}</button>
          ))}
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div>
            <div style={lbl}>Execution Server</div>
            <select value={serverId} onChange={e => setServerId(e.target.value)} style={input}>
              <option value="">Select server…</option>
              {(servers||[]).map(s => <option key={s.id} value={s.id}>{s.server_name}</option>)}
            </select>
          </div>

          {mode === 'upload' ? (
            <div>
              <div style={lbl}>Documents (multiple allowed)</div>
              <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.html,.htm"
                onChange={e => setFiles([...e.target.files])} style={{ fontSize:12 }} />
              {files.length > 0 && (
                <div style={{ fontSize:11, color:'#64748B', marginTop:5 }}>
                  {files.length} file{files.length>1?'s':''} selected
                </div>
              )}
            </div>
          ) : (
            <>
              <div>
                <div style={lbl}>File path on server</div>
                <input value={path} onChange={e => setPath(e.target.value)}
                  placeholder="/data/corpus/manual.pdf" style={{...input, fontFamily:'var(--mono)', fontSize:12}} />
                <div style={{ fontSize:10.5, color:'#94A3B8', marginTop:4 }}>
                  Absolute path on the selected execution server. The file is fetched, processed,
                  and the derived datasource placed back on the same server.
                </div>
              </div>
              <div>
                <div style={lbl}>Name (optional)</div>
                <input value={docName} onChange={e => setDocName(e.target.value)}
                  placeholder="Defaults to file name" style={input} />
              </div>
            </>
          )}

          <div>
            <div style={lbl}>Chunking</div>
            <select value={strategy} onChange={e => setStrategy(e.target.value)} style={input}>
              <option value="paragraph">Paragraph (recommended)</option>
              <option value="fixed_window">Fixed window (tokens)</option>
            </select>
            {strategy === 'fixed_window' && (
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{...lbl, fontSize:10}}>Window</div>
                  <input type="number" value={sizeTok} min={20}
                    onChange={e => setSizeTok(+e.target.value)} style={input}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{...lbl, fontSize:10}}>Overlap</div>
                  <input type="number" value={overTok} min={0}
                    onChange={e => setOverTok(+e.target.value)} style={input}/>
                </div>
              </div>
            )}
          </div>

          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12.5,
            color:'#0D1B2E', cursor:'pointer', userSelect:'none' }}>
            <input type="checkbox" checked={merge} onChange={e => setMerge(e.target.checked)} />
            Merge into one corpus (document set)
          </label>
          {merge && (
            <div>
              <div style={lbl}>Corpus name</div>
              <input value={setName} onChange={e => setSetName(e.target.value)}
                placeholder="e.g. Support Knowledge Base" style={input} />
            </div>
          )}

          {err && <Err msg={err} />}
          <button className="btn btn-primary" onClick={submit} disabled={busy}
            style={{ justifyContent:'center' }}>
            {busy ? <><Spinner size={12}/> Working…</>
                  : mode==='upload' ? 'Upload & Process' : 'Register & Process'}
          </button>
        </div>
      </div>

      {/* ── Right: status lists ───────────────────────────────────────────── */}
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

        {(sets||[]).length > 0 && (
          <div style={{ background:'#FFF', border:'1px solid #E4EBF5', borderRadius:12, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #EEF2FA',
              fontSize:13, fontWeight:600 }}>Corpus Sets</div>
            {(sets||[]).map((s,i) => (
              <div key={s.set_id} style={{ padding:'11px 16px', display:'flex', alignItems:'center',
                gap:10, borderBottom: i<sets.length-1 ? '1px solid #F0F4F8':'none' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>{s.set_name}</div>
                  <div style={{ fontSize:11, color:'#64748B' }}>
                    {s.documents_chunked}/{s.documents_total} documents processed
                    {s.error_message ? ` · ${s.error_message}` : ''}
                  </div>
                </div>
                <Badge s={s.status} />
                {['OPEN','FAILED'].includes(s.status)
                  && s.documents_total > 0
                  && s.documents_chunked === s.documents_total && (
                  <button className="btn btn-primary btn-sm" onClick={() => finalizeSet(s.set_id)}>
                    Merge → Datasource
                  </button>
                )}
                {s.status === 'READY' && (
                  <button className="btn btn-ghost btn-sm" onClick={onDatasourceReady}>
                    View datasource →
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ background:'#FFF', border:'1px solid #E4EBF5', borderRadius:12, overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #EEF2FA',
            fontSize:13, fontWeight:600 }}>Documents</div>
          {(docs||[]).length === 0 ? (
            <div style={{ padding:'30px 16px', textAlign:'center', fontSize:12, color:'#94A3B8' }}>
              No documents yet. Upload files or register a path to begin.
            </div>
          ) : (docs||[]).map((d,i) => (
            <div key={d.document_id} style={{ padding:'10px 16px', display:'flex',
              alignItems:'center', gap:10, borderBottom: i<docs.length-1 ? '1px solid #F0F4F8':'none' }}>
              <span className="badge b-slate" style={{ flexShrink:0 }}>{d.file_type}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12.5, fontWeight:500, whiteSpace:'nowrap',
                  overflow:'hidden', textOverflow:'ellipsis' }}>{d.doc_name}</div>
                {d.error_message && (
                  <div style={{ fontSize:10.5, color:'var(--red)' }}>{d.error_message}</div>
                )}
                {(d.set_id || d.chunk_count != null) && (
                  <div style={{ fontSize:10, color:'#94A3B8' }}>
                    {d.chunk_count != null ? `${d.chunk_count} chunks` : ''}
                    {d.chunk_count != null && d.set_id ? ' · ' : ''}
                    {d.set_id ? 'part of corpus set' : ''}
                  </div>
                )}
              </div>
              {ACTIVE.includes(d.status) && <Spinner size={11}/>}
              <Badge s={d.status} />
              {d.status === 'READY' && d.derived_datasource_id && (
                <button className="btn btn-ghost btn-sm" onClick={onDatasourceReady}>
                  View →
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
